import useRouter from "common/hooks/use-router";
import useAuth from "hooks/useAuth";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { User } from "types";
import type { TableColumn } from "common/components/DataTable/DataTable";
import CrudList from "common/components/Crud/CrudList";
import type { CrudHeaderAction } from "common/components/Crud/components/CrudHeader";
import type { CrudFilter } from "common/components/Crud/components/CrudFilters";
import CrudEdit from "common/components/Crud/CrudEdit";
import UserForm from "./UserForm";
import Badges from "components/badges/Badges";

function SidebarUserForm({ ...props }: any) {
  return <CrudEdit collectionName="users" Form={UserForm} {...props} />;
}

function Users() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { user: currentUser } = useAuth();

  const canManageUsers = currentUser?.isAdmin || currentUser?.isSuperadmin;

  const handleDetail = useCallback(
    (id: any) => {
      navigate(`/users/edit/${id}`);
    },
    [navigate]
  );

  const columns: TableColumn<User>[] = useMemo(
    () => [
      {
        field: "username",
        header: t("users.username"),
        sortable: true,
        renderType: "bold",
      },
      {
        field: "email",
        header: t("users.email"),
        sortable: true,
      },
      {
        field: "confirmed",
        header: t("users.confirmed"),
        customRender: (rowData: any) =>
          rowData.confirmed ? (
            <Badges
              items={[{ name: t("users.confirmed") }]}
              iconName="CheckCircleIcon"
              className="text-green-600 [&_span]:border-green-400 [&_span]:text-green-700"
            />
          ) : (
            <Badges
              items={[{ name: t("forms.unconfirmed") }]}
              iconName="ExclamationCircleIcon"
              className="text-red-600 [&_span]:border-red-300 [&_span]:text-red-600"
            />
          ),
      },
    ],
    [t]
  );

  const actions: CrudHeaderAction[] = useMemo(() => {
    return [
      {
        title: t("common.new"),
        icon: "PlusCircleIconSolid",
        action: "openSidebar",
        type: "white",
        disabled: !canManageUsers,
        className: "flex flex-row-reverse",
      },
    ];
  }, [canManageUsers, t]);

  const filters: CrudFilter[] = useMemo(() => {
    return [
      {
        name: "username$containsi",
        label: t("users.username"),
        renderType: "input",
      },
      {
        name: "email$containsi",
        label: t("users.email"),
        renderType: "input",
      },
    ];
  }, [t]);

  return (
    <CrudList
      collectionName="users"
      columns={columns}
      headerActions={actions}
      filters={filters}
      defaultSort="username:ASC"
      canDelete={canManageUsers}
      canEdit={canManageUsers}
      icon="UsersIcon"
      onNavigate={handleDetail}
      formMode="sidebar"
      SidebarFormComponent={SidebarUserForm}
      actionsHeader=""
    />
  );
}

export default Users;
